import { useMemo } from 'react'

import { useSnapshot } from 'valtio'

import { useCommandHotkeys } from '@/shared/commands/command.hooks'
import { useFocusWithin } from '@/shared/ui/useFocusWithin'
import { isEmptyArray } from '@/utils/primitives'

import {
  cellCommands,
  useCellCommandContext,
  type CellCommandContext,
} from '../cell/cell.commands'
import { CellContext } from '../cell/cell.context'
import { parseCellKey } from '../cell/cell.schema'
import { useColumnsDerived } from '../column/column.derived'
import { useVisibleIndexedCues } from '../cue/cue.hooks'
import { getColumn } from '../data/columns.store'
import { getCue } from '../data/cues.store'
import { modalStore } from '../modals/modal.store'

import type { GridNavigationCommandContext } from './grid.commands'
import { gridNavigationCommands } from './grid.commands'
import type { GridSelection } from './grid.store'
import {
  gridSelectionActions,
  gridState,
  gridStateActions,
  useGridSnap,
} from './grid.store'

export function GridHotkeys({
  gridRef,
  gridContextMenuRef,
}: {
  gridRef: React.RefObject<HTMLDivElement | null>
  gridContextMenuRef: React.RefObject<HTMLDivElement | null>
}) {
  // Stable ref to roots
  const additionalFocusRoots = useMemo(
    () => [gridContextMenuRef],
    [gridContextMenuRef],
  )

  // Grid focus determines hotkey scope
  const gridHasFocus = useFocusWithin(gridRef, {
    additionalRoots: additionalFocusRoots,
  })

  // Modals should block grid-scoped hotkeys
  const modalStack = useSnapshot(modalStore).stack
  const anyModalsOpen = !isEmptyArray(modalStack)

  const isKeyboardBlocked = anyModalsOpen

  // Combine into grid scope
  const gridScopeEnabled = gridHasFocus && !isKeyboardBlocked

  return (
    <>
      <GridCellHotkeys scopeEnabled={gridScopeEnabled} />
      <GridNavigationHotkeys scopeEnabled={gridScopeEnabled} />
    </>
  )
}

type HotkeyComponentProps = { scopeEnabled: boolean }

function GridNavigationHotkeys({ scopeEnabled }: HotkeyComponentProps) {
  const visibleCues = useVisibleIndexedCues()
  const { visibleColumns } = useColumnsDerived()

  const snapshot = useSnapshot(gridState)

  const context: GridNavigationCommandContext = {
    selection: snapshot.selection as GridSelection,

    model: {
      cues: visibleCues,
      columns: visibleColumns,
    },

    isEditing: snapshot.editingCell !== null,

    select: gridSelectionActions.select,
  }

  useCommandHotkeys(gridNavigationCommands, context, {
    scopeEnabled: scopeEnabled,
  })

  return null
}

function GridCellHotkeys({ scopeEnabled }: HotkeyComponentProps) {
  const selection = useGridSnap().selection

  if (selection?.type !== 'cell' || !selection?.cellKey) return null

  // The selection outlives what it points at. On a stale cellKey the cell hooks
  // below throw, taking the whole grid down, so sit out until one is live again.
  const { cueId, columnId } = parseCellKey(selection.cellKey)
  if (!getCue(cueId) || !getColumn(columnId)) return null

  return (
    <CellContext.Provider value={selection.cellKey}>
      <SelectedCellHotkeys scopeEnabled={scopeEnabled} />
    </CellContext.Provider>
  )
}

function SelectedCellHotkeys({ scopeEnabled }: HotkeyComponentProps) {
  const context = useCellCommandContext()

  useCommandHotkeys<CellCommandContext>(cellCommands, context, {
    scopeEnabled: scopeEnabled,
    onExecuted: () => {
      // Close CellContextMenu after command hotkey executes
      gridStateActions.contextMenu.close()
    },
  })

  return null
}
